import {
  useContext,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Icon } from '@chakra-ui/react'
import { HiOutlineFolderAdd } from 'react-icons/hi'
import { IoMdRefresh } from 'react-icons/io'
import { BsGrid, BsListUl, BsSortDownAlt, BsSortUpAlt } from 'react-icons/bs'
import { AiOutlinePicture } from 'react-icons/ai'
import { useRouteMatch, useHistory } from 'react-router-dom'
import { MdDriveFolderUpload, MdUploadFile } from 'react-icons/md'
import {
  MLViewType,
  MLSortParams,
  ACLPermissionEnum,
  MLSortDirection,
} from '../../../constants'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { MLContext } from '../../../providers'
import { isRestrictedUser, isViewOnlyUser } from '../../../../utils/roles'
import { UserContext } from '../../../providers/UserProvider'

const MenuContainer = styled.div<{
  isVisible: boolean
  top: number
  left: number
}>`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  background-color: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  z-index: 1000;
  display: ${props => (props.isVisible ? 'block' : 'none')};
  min-width: 200px;
  font-size: 14px;
`

const MenuItemContainer = styled.div`
  padding: 6px 0;
`

const MenuItem = styled.div<{ selected?: boolean }>`
  padding: 4px 12px;
  cursor: pointer;
  color: #000000;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #f7fafc;
  }

  svg {
    font-size: 16px;
    color: #4a5568;
  }

  &.disabled {
    color: #e2e8f0;
    svg {
      color: #e2e8f0;
    }
    cursor: not-allowed;
  }

  ${props =>
    props.selected &&
    `
    background-color: #f0f4f8;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      right: 12px;
      width: 16px;
      height: 16px;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%234a5568' d='M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }
  `}
`

// eslint-disable-next-line @typescript-eslint/member-delimiter-style
const SubMenu = styled.div<{ shouldOpenLeft?: boolean; alignBottom?: boolean }>`
  position: absolute;
  ${props => (props.shouldOpenLeft ? 'right: 100%;' : 'left: 100%;')}
  ${props => props.alignBottom && 'bottom: 0%;'}
  background-color: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  min-width: 180px;
  display: none;
  overflow-y: auto;
  max-height: 350px;
  padding: 2px 0;
`

const MenuItemWithSubmenu = styled(MenuItem)<{ shouldOpenLeft?: boolean }>`
  position: relative;
  padding-right: 24px;

  &:after {
    content: '${props => (props.shouldOpenLeft ? '‹' : '›')}';
    position: absolute;
    right: 8px;
  }

  &:hover ${SubMenu} {
    display: block;
  }

  &.disabled {
    color: #e2e8f0;
    svg {
      color: #e2e8f0;
    }
    cursor: not-allowed;

    &:hover ${SubMenu} {
      display: none;
    }
  }
`

const Divider = styled.div`
  height: 1px;
  background-color: #e2e8f0;
  margin: 4px 0;
`

interface ContextMenuPosition {
  x: number
  y: number
}

export interface RightClickContextMenuRef {
  handleContextMenu(e: MouseEvent): void
  closeMenu(): void
}

export const RightClickContextMenu = forwardRef<
  RightClickContextMenuRef,
  unknown
>((_, ref) => {
  const routerHistory = useHistory()
  const { user } = useContext(UserContext)
  const { url } = useRouteMatch()
  const [state, dispatch] = useContext(MLContext)
  const { t } = useTranslation()

  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<ContextMenuPosition>({ x: 0, y: 0 })

  const [submenuPositions, setSubmenuPositions] = useState<{
    [key: string]: boolean
  }>({
    view: false,
    sort: false,
  })

  const [shouldAlignBottom, setShouldAlignBottom] = useState<{
    [key: string]: boolean
  }>({
    view: false,
    sort: false,
  })

  const isMenuItemDisabled = (itemSlug: string): boolean => {
    /**
     * Logic for determining if the menu item should be disabled
     *
     * 1. If the user is restricted and does not have contribute or manage permissions,
     *    then the menu item should be disabled
     * 2. If the user is on a media collection page, then the menu item should be disabled
     *    except for folders in the media collection page (depending on the user's permissions)
     */
    let isDisabled = false

    // View only users should not be able to access the context menu items
    if (isViewOnlyUser(user?.roles)) {
      isDisabled = true
    }

    if (user?.roles && isRestrictedUser(user.roles)) {
      if (
        !(
          state.folderPermission === ACLPermissionEnum.CONTRIBUTE ||
          state.folderPermission === ACLPermissionEnum.MANAGE
        )
      ) {
        // disable for all media collection / library pages where the user
        // has no contribute or manage permissions
        isDisabled = true
      }
    }

    if (state.mediaCollectionRoute) {
      isDisabled = true // disable for all media collections pages

      // with an exception for folders in the media collection page
      if (url.includes(`/media-collections/${state.currentMediaCollection}/`)) {
        const urlKey = url.split(
          `/media-collections/${state.currentMediaCollection}/`,
        )

        if (urlKey.length && urlKey[1] !== '') {
          isDisabled = false
        }

        if (user?.roles && isRestrictedUser(user.roles)) {
          if (
            !(
              state.folderPermission === ACLPermissionEnum.CONTRIBUTE ||
              state.folderPermission === ACLPermissionEnum.MANAGE
            )
          ) {
            // disable for all media collection / library pages where the user
            // has no contribute or manage permissions
            isDisabled = true
          }
        }
      }
    }

    switch (itemSlug) {
      case 'new_folder':
      case 'file_upload':
      case 'folder_upload':
        return isDisabled
      default:
        return false
    }
  }

  useImperativeHandle(ref, () => ({
    handleContextMenu: (e: MouseEvent) => {
      // Check if the right-click is on a blue strip or header area
      const target = e.target as HTMLElement
      const isRightClickDisabledClass = target.closest(
        '.no-right-click-context-menu',
      )

      if (isRightClickDisabledClass) {
        return // Do not open right-click context menu
      }

      e.preventDefault()

      // Calculate available space on both sides
      const rightSpace = window.innerWidth - e.clientX
      const bottomSpace = window.innerHeight - e.clientY

      // Base menu position
      let x = e.clientX
      const y = Math.min(e.clientY, window.innerHeight - 300)

      // If right space is not enough for base menu, shift it left
      if (rightSpace < 200) {
        // 200px is base menu width
        x = Math.max(0, window.innerWidth - 200)
      }

      setPosition({ x, y })
      handleCloseFileFolderContextMenu()
      setIsVisible(true)

      // Calculate submenu positions
      const shouldOpenLeft: boolean = rightSpace < 380 // 200 + 180 (base + submenu width)
      const alignBottom: boolean = bottomSpace < 415 // 300 + 115 (submenu height + 115px)
      setSubmenuPositions({
        view: shouldOpenLeft,
        sort: shouldOpenLeft,
      })
      setShouldAlignBottom({
        view: alignBottom,
        sort: alignBottom,
      })
    },
    closeMenu: () => {
      closeMenu()
    },
  }))

  const closeMenu = (): void => {
    setIsVisible(false)
  }

  useEffect(() => {
    const handleClickOutside = (): void => {
      if (isVisible) {
        closeMenu()
      }
    }

    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isVisible) {
        closeMenu()
      }
    }

    document.addEventListener('click', handleClickOutside)
    document.addEventListener('contextmenu', closeMenu)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('contextmenu', closeMenu)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isVisible])

    useEffect(() => {
    if(state.contextMenu.visible) {
      setIsVisible(false)
    }
  }, [state.contextMenu.visible])

  const handleCloseFileFolderContextMenu = (): void => {
    dispatch({ type: MediaLibraryActions.CLOSE_CONTEXT_MENU })
  }

  const handleCreateFolder = (): void => {
    document.dispatchEvent(new CustomEvent('ik-new-folder-modal-open'))
    closeMenu()
  }

  const handleFileUpload = (): void => {
    document.dispatchEvent(new CustomEvent('ik-file-upload-modal-open'))
    closeMenu()
  }

  const handleFolderUpload = (): void => {
    document.dispatchEvent(new CustomEvent('ik-folder-upload-modal-open'))
    closeMenu()
  }

  const handleViewChange = (viewType: MLViewType): void => {
    dispatch({
      type: MediaLibraryActions.SET_VIEW,
      payload: viewType,
    })
    closeMenu()
  }

  const handleSortChange = (sortParam: MLSortParams): void => {
    if (state.sortDescriptors.sortParam === sortParam) {
      dispatch({
        type: MediaLibraryActions.SET_SORT_DIRECTION,
        payload:
          state.sortDescriptors.sortDirection === MLSortDirection.ASC
            ? MLSortDirection.DESC
            : MLSortDirection.ASC,
      })
    } else {
      dispatch({
        type: MediaLibraryActions.SET_SORT_PARAM,
        payload: sortParam,
      })
    }
    closeMenu()
  }

  const handleSortDirectionChange = (direction: MLSortDirection): void => {
    dispatch({
      type: MediaLibraryActions.SET_SORT_DIRECTION,
      payload: direction,
    })
  }

  const handleRefresh = (): void => {
    void state.refetch()
    closeMenu()
  }

  return (
    <MenuContainer isVisible={isVisible} top={position.y} left={position.x}>
      <MenuItemContainer>
        <MenuItem
          onClick={
            isMenuItemDisabled('new_folder') ? undefined : handleCreateFolder
          }
          className={isMenuItemDisabled('new_folder') ? 'disabled' : ''}
        >
          <Icon as={HiOutlineFolderAdd} />
          {t('new_folder', 'New Folder')}
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={
            isMenuItemDisabled('file_upload') ? undefined : handleFileUpload
          }
          className={isMenuItemDisabled('file_upload') ? 'disabled' : ''}
        >
          <Icon as={MdUploadFile} />
          {t('file_upload', 'File Upload')}
        </MenuItem>

        <MenuItem
          onClick={
            isMenuItemDisabled('folder_upload') ? undefined : handleFolderUpload
          }
          className={isMenuItemDisabled('folder_upload') ? 'disabled' : ''}
        >
          <Icon as={MdDriveFolderUpload} />
          {t('folder_upload', 'Folder Upload')}
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={
            isMenuItemDisabled('gen_ai_image')
              ? undefined
              : () => {
                  routerHistory.push(
                    '/media-library/generate-images?tab=generate',
                  )
                  closeMenu()
                }
          }
          className={isMenuItemDisabled('gen_ai_image') ? 'disabled' : ''}
        >
          <Icon as={AiOutlinePicture} />
          {t('gen_ai_image', 'Generate Image')}
          <span
            // eslint-disable-next-line react/forbid-dom-props
            style={{
              marginLeft: '0px',
              color: '#000000',
              fontSize: '10px',
              backgroundColor: '#E2E8F0',
              padding: '2px 6px',
              borderRadius: '6px',
            }}
          >
            AI
          </span>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={isMenuItemDisabled('refresh') ? undefined : handleRefresh}
          className={isMenuItemDisabled('refresh') ? 'disabled' : ''}
        >
          <Icon as={IoMdRefresh} />
          {t('refresh', 'Refresh')}
        </MenuItem>

        <MenuItemWithSubmenu
          shouldOpenLeft={submenuPositions.view}
          className={isMenuItemDisabled('view') ? 'disabled' : ''}
        >
          <Icon as={state.view === MLViewType.GRID ? BsListUl : BsGrid} />
          {t('view', 'View')}
          <SubMenu
            shouldOpenLeft={submenuPositions.view}
            className={isMenuItemDisabled('view') ? 'disabled' : ''}
            alignBottom={shouldAlignBottom.view}
          >
            <MenuItem
              onClick={() => void handleViewChange(MLViewType.GRID)}
              selected={state.view === MLViewType.GRID}
            >
              <Icon as={BsGrid} />
              {t('as_grid', 'as Grid')}
            </MenuItem>
            <MenuItem
              onClick={() => void handleViewChange(MLViewType.LIST)}
              selected={state.view === MLViewType.LIST}
            >
              <Icon as={BsListUl} />
              {t('as_list', 'as List')}
            </MenuItem>
          </SubMenu>
        </MenuItemWithSubmenu>

        <MenuItemWithSubmenu
          shouldOpenLeft={submenuPositions.sort}
          className={isMenuItemDisabled('sort') ? 'disabled' : ''}
        >
          <Icon
            as={
              state.sortDescriptors.sortDirection === 'DESC'
                ? BsSortDownAlt
                : BsSortUpAlt
            }
          />
          {t('sort_by', 'Sort by')}
          <SubMenu
            shouldOpenLeft={submenuPositions.sort}
            className={isMenuItemDisabled('sort') ? 'disabled' : ''}
            alignBottom={shouldAlignBottom.sort}
          >
            <MenuItem
              onClick={() =>
                void handleSortDirectionChange(MLSortDirection.ASC)
              }
              selected={
                state.sortDescriptors.sortDirection === MLSortDirection.ASC
              }
            >
              <Icon as={BsSortDownAlt} />
              {t('ascending', 'Ascending')}
            </MenuItem>
            <MenuItem
              onClick={() =>
                void handleSortDirectionChange(MLSortDirection.DESC)
              }
              selected={
                state.sortDescriptors.sortDirection === MLSortDirection.DESC
              }
            >
              <Icon as={BsSortUpAlt} />
              {t('descending', 'Descending')}
            </MenuItem>

            <Divider style={{ marginBottom: '4px' }} />

            <MenuItem
              onClick={() => void handleSortChange(MLSortParams.NAME)}
              selected={state.sortDescriptors.sortParam === MLSortParams.NAME}
            >
              {t('name', 'Name')}
            </MenuItem>
            <MenuItem
              onClick={() => void handleSortChange(MLSortParams.CREATED)}
              selected={
                state.sortDescriptors.sortParam === MLSortParams.CREATED
              }
            >
              {t('creation_date', 'Creation Date')}
            </MenuItem>
            <MenuItem
              onClick={() => void handleSortChange(MLSortParams.UPDATED)}
              selected={
                state.sortDescriptors.sortParam === MLSortParams.UPDATED
              }
            >
              {t('last_updated_date', 'Last Updated Date')}
            </MenuItem>
            <MenuItem
              onClick={() => void handleSortChange(MLSortParams.SIZE)}
              selected={state.sortDescriptors.sortParam === MLSortParams.SIZE}
            >
              {t('file_size', 'File Size')}
            </MenuItem>
            <MenuItem
              onClick={() => void handleSortChange(MLSortParams.HEIGHT)}
              selected={state.sortDescriptors.sortParam === MLSortParams.HEIGHT}
            >
              {t('image_height', 'Image Height')}
              {state.sortDescriptors.sortParam === MLSortParams.HEIGHT && (
                <Icon as={BsSortDownAlt} />
              )}
            </MenuItem>
            <MenuItem
              onClick={() => void handleSortChange(MLSortParams.WIDTH)}
              selected={state.sortDescriptors.sortParam === MLSortParams.WIDTH}
            >
              {t('image_width', 'Image Width')}
            </MenuItem>
            <MenuItem
              onClick={() => void handleSortChange(MLSortParams.RELEVANCE)}
              selected={
                state.sortDescriptors.sortParam === MLSortParams.RELEVANCE
              }
            >
              {t('relevance', 'Relevance')}
            </MenuItem>
          </SubMenu>
        </MenuItemWithSubmenu>
      </MenuItemContainer>
    </MenuContainer>
  )
})

export default RightClickContextMenu
